import { z } from 'zod';

export const TRAFFIC_CARDS = [
  'pages',
  'acquisition',
  'locations',
  'technology',
] as const;
export type TrafficCard = (typeof TRAFFIC_CARDS)[number];

// `until_data` brings a card back by itself once it has something to show.
export const HIDE_MODES = ['until_data', 'always'] as const;
export type HideMode = (typeof HIDE_MODES)[number];

export const MAX_OVERVIEW_EVENTS = 3;

// Sections, traffic cards (`traffic.pages`) and added widgets (`widget:<id>`).
const LAYOUT_ID =
  /^(accounts|traffic|events|traffic\.(pages|acquisition|locations|technology)|widget:[A-Za-z0-9_-]{1,64})$/;
const layoutId = z.string().regex(LAYOUT_ID);
const eventName = z.string().min(1).max(200);
const widgetId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
const trafficOrder = z.array(z.enum(TRAFFIC_CARDS)).max(TRAFFIC_CARDS.length);
const overview = z.array(eventName).max(MAX_OVERVIEW_EVENTS);

export const dashboardLayoutSchema = z.object({
  hidden: z.record(layoutId, z.enum(HIDE_MODES)).default({}),
  hiddenEvents: z.array(eventName).max(1000).default([]),
  trafficOrder: trafficOrder.default([]),
  overview: overview.default([]),
});
export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;

// A personal layout stores only what differs from the project's, so later project changes still reach
// everyone. `shown` un-hides a card the project hides; `events` maps an event name to hidden or not.
export const dashboardLayoutPatchSchema = z.object({
  hidden: z.record(layoutId, z.enum([...HIDE_MODES, 'shown'])).optional(),
  events: z.record(eventName, z.boolean()).optional(),
  trafficOrder: trafficOrder.optional(),
  widgetOrder: z.array(widgetId).max(200).optional(),
  overview: overview.optional(),
});
export type DashboardLayoutPatch = z.infer<typeof dashboardLayoutPatchSchema>;

export type EffectiveDashboardLayout = DashboardLayout & {
  widgetOrder?: string[];
};

// Save for Everyone: the layout, the added widgets' order, and the ones to remove. Removal is explicit, so a
// widget someone added after this editor loaded is kept rather than dropped for being unlisted.
export const saveProjectLayoutSchema = z.object({
  layout: dashboardLayoutSchema,
  widgetIds: z.array(widgetId).max(200),
  removedWidgetIds: z.array(widgetId).max(200).default([]),
});
export type SaveProjectLayoutInput = z.infer<typeof saveProjectLayoutSchema>;

const sameList = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

export function layoutPatch(
  project: DashboardLayout,
  edited: DashboardLayout,
  projectWidgetIds: readonly string[],
  editedWidgetIds: readonly string[],
): DashboardLayoutPatch {
  const patch: DashboardLayoutPatch = {};

  const hidden: NonNullable<DashboardLayoutPatch['hidden']> = {};
  for (const id of new Set([
    ...Object.keys(project.hidden),
    ...Object.keys(edited.hidden),
  ])) {
    const before = project.hidden[id];
    const after = edited.hidden[id];
    if (before === after) continue;
    hidden[id] = after ?? 'shown';
  }
  if (Object.keys(hidden).length > 0) patch.hidden = hidden;

  const events: Record<string, boolean> = {};
  const wasHidden = new Set(project.hiddenEvents);
  const isHidden = new Set(edited.hiddenEvents);
  for (const name of new Set([...wasHidden, ...isHidden])) {
    if (wasHidden.has(name) !== isHidden.has(name))
      events[name] = isHidden.has(name);
  }
  if (Object.keys(events).length > 0) patch.events = events;

  if (!sameList(project.trafficOrder, edited.trafficOrder))
    patch.trafficOrder = edited.trafficOrder;
  if (!sameList(project.overview, edited.overview))
    patch.overview = edited.overview;
  if (!sameList(projectWidgetIds, editedWidgetIds))
    patch.widgetOrder = [...editedWidgetIds];

  return patch;
}

export function applyLayoutPatch(
  project: DashboardLayout,
  patch: DashboardLayoutPatch,
): EffectiveDashboardLayout {
  const hidden = { ...project.hidden };
  for (const [id, mode] of Object.entries(patch.hidden ?? {})) {
    if (mode === 'shown') delete hidden[id];
    else hidden[id] = mode;
  }

  const events = patch.events ?? {};
  const hiddenEvents = project.hiddenEvents.filter(
    (name) => events[name] !== false,
  );
  for (const [name, hide] of Object.entries(events)) {
    if (hide && !hiddenEvents.includes(name)) hiddenEvents.push(name);
  }

  return {
    hidden,
    hiddenEvents,
    trafficOrder: patch.trafficOrder ?? project.trafficOrder,
    overview: patch.overview ?? project.overview,
    ...(patch.widgetOrder && { widgetOrder: patch.widgetOrder }),
  };
}

export const setLayoutHidden = (
  layout: DashboardLayout,
  id: string,
  mode: HideMode | undefined,
): DashboardLayout => {
  const hidden = { ...layout.hidden };
  if (mode) hidden[id] = mode;
  else delete hidden[id];
  return { ...layout, hidden };
};

export const isLayoutHidden = (
  layout: DashboardLayout,
  id: string,
  hasData: boolean,
): boolean => {
  const mode = layout.hidden[id];
  return mode === 'always' || (mode === 'until_data' && !hasData);
};

export const orderedTrafficCards = (layout: DashboardLayout): TrafficCard[] => [
  ...new Set([...layout.trafficOrder, ...TRAFFIC_CARDS]),
];

export const orderedWidgetIds = (
  projectIds: readonly string[],
  order: readonly string[] | undefined,
): string[] => [
  ...new Set([
    ...(order ?? []).filter((id) => projectIds.includes(id)),
    ...projectIds,
  ]),
];
